import { useEffect, useState } from 'react';
import { Search } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import { clsx } from 'clsx';
import { usePerformanceContext } from '../../PerformanceContext';
import { useCampaignFilters } from '../hooks/useCampaignFilters';

const PRESETS: { label: string; days: number | 'lifetime' }[] = [
  { label: '7d', days: 7 },
  { label: '28d', days: 28 },
  { label: '90d', days: 90 },
  { label: 'All time', days: 'lifetime' },
];

function rangeForPreset(p: { days: number | 'lifetime' }): { from: string | null; to: string | null } {
  if (p.days === 'lifetime') return { from: null, to: null };
  const today = new Date();
  const start = new Date(today);
  start.setDate(today.getDate() - p.days);
  return { from: start.toISOString().slice(0, 10), to: today.toISOString().slice(0, 10) };
}

export function CampaignFiltersBar() {
  const { dateFrom, dateTo, setDateRange } = usePerformanceContext();
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: filtersData } = useCampaignFilters();

  // Local debounced search input state.
  const initial = searchParams.get('search') ?? '';
  const [searchInput, setSearchInput] = useState(initial);

  useEffect(() => {
    const t = setTimeout(() => {
      const next = new URLSearchParams(searchParams);
      if (searchInput) next.set('search', searchInput);
      else next.delete('search');
      // Reset page on filter change.
      next.delete('page');
      if (next.toString() !== searchParams.toString()) {
        setSearchParams(next, { replace: true });
      }
    }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput]);

  const objective = searchParams.get('objective') ?? '';

  function setObjective(v: string) {
    const next = new URLSearchParams(searchParams);
    if (v) next.set('objective', v);
    else next.delete('objective');
    next.delete('page');
    setSearchParams(next, { replace: true });
  }

  function activePresetLabel(): string | null {
    if (!dateFrom && !dateTo) return 'All time';
    if (!dateFrom || !dateTo) return null;
    for (const p of PRESETS) {
      const r = rangeForPreset(p);
      if (r.from === dateFrom && r.to === dateTo) return p.label;
    }
    return null;
  }

  const active = activePresetLabel();

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-3 flex flex-wrap items-center gap-3">
      <div className="flex items-center gap-1">
        <span className="text-[10px] font-mono uppercase text-slate-400 mr-1">
          Date
        </span>
        {PRESETS.map((p) => {
          const isActive = active === p.label;
          return (
            <button
              key={p.label}
              type="button"
              onClick={() => setDateRange(rangeForPreset(p))}
              className={clsx(
                'rounded-full border px-3 py-1 text-xs',
                isActive
                  ? 'border-teal-500 bg-teal-50 text-teal-700'
                  : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50',
              )}
            >
              {p.label}
            </button>
          );
        })}
      </div>

      <label className="flex items-center gap-2 text-xs">
        <span className="text-[10px] font-mono uppercase text-slate-400">
          Objective
        </span>
        <select
          value={objective}
          onChange={(e) => setObjective(e.target.value)}
          className="rounded border border-slate-200 bg-white px-2 py-1 text-xs"
        >
          <option value="">All</option>
          {filtersData?.objectives.map((o) => (
            <option key={o} value={o}>
              {o.replace(/^OUTCOME_/, '')}
            </option>
          ))}
        </select>
      </label>

      <label className="flex items-center gap-2 flex-1 min-w-[200px]">
        <span className="text-[10px] font-mono uppercase text-slate-400">
          Search
        </span>
        <span className="flex-1 flex items-center gap-2 rounded border border-slate-200 bg-white px-2 py-1">
          <Search size={12} className="text-slate-400" />
          <input
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search campaigns by name…"
            className="w-full text-xs outline-none placeholder:text-slate-400"
          />
        </span>
      </label>
    </div>
  );
}
